/* eslint-disable camelcase -- GA4 ecommerce schema uses snake_case field names
   (item_id, item_name, transaction_id, …) and the dataLayer is forwarded to
   Google Tag Manager / GA4 with these exact keys. */
import type { Money } from 'gportal-grpc/gportal/type/v1/money_pb';
import type { MoneyLike } from '~/composables/useNumberMoneyFormatting';

type MoneyInput = Omit<Money, 'display'> | MoneyLike;

export interface GaEcommerceItem {
  /** product_key from Strapi / checkout store — stable across LP and checkout. */
  item_id: string;
  item_name?: string;
  item_category?: string;
  item_variant?: string;
  price?: number;
  quantity?: number;
}

interface GaEcommercePayload {
  currency?: string;
  value?: number;
  items: GaEcommerceItem[];
  transaction_id?: string;
  coupon?: string;
}

function moneyToFloat(money: MoneyInput): number {
  const fraction = money.fraction ?? 0;
  return Number(money.amount) / 10 ** fraction;
}

function getDataLayer(): Array<Record<string, unknown>> | null {
  if (typeof window === 'undefined') return null;
  const w = window as Window & {
    dataLayer?: Array<Record<string, unknown>>;
  };
  w.dataLayer = w.dataLayer || [];
  return w.dataLayer;
}

/**
 * The GTM container object, populated only once `gtm.js` has actually run.
 * Absent when GTM is ad-blocked or hasn't loaded — in that case nothing will
 * ever invoke a dataLayer `eventCallback`, so a caller waiting on one (see
 * `pushEcommerceEvent`) can resolve immediately instead of stalling for the
 * full fallback timeout.
 */
function isGtmLoaded(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    (window as Window & { google_tag_manager?: unknown }).google_tag_manager !=
    null
  );
}

interface PushOptions {
  /**
   * Invoked once GTM has fired the tags for this push (GTM's `eventCallback`),
   * or after `timeoutMs` as a fallback if GTM is absent/slow. Use this to defer
   * a navigation until the hit has actually been sent — see `trackPurchase`.
   */
  onSent?: () => void;
  timeoutMs?: number;
  /**
   * Flat top-level dataLayer keys pushed alongside `event`/`ecommerce`, for GTM
   * variables that read the dataLayer root rather than the `ecommerce` object
   * (e.g. `transactionContactEmail` for Enhanced Conversions). Every key used
   * here must also be listed in `FLAT_KEY_RESET`.
   */
  extra?: Record<string, unknown>;
}

/**
 * Flat keys reset to `undefined` on every push, so a value set by one event
 * never survives into the next.
 *
 * GTM's data model keeps the last value pushed for a key until something
 * overwrites it, and this is an SPA — a `purchase` followed by a client-side
 * navigation (the result page's success screen links home with a NuxtLink, and
 * stays put whenever the checkout has no redirect URL) would otherwise leave
 * the buyer's address resolving on every later `view_item`/`add_to_cart`/
 * `begin_checkout` in the same page session. The legacy shop is an MPA, so a
 * full page load clears its model for free; ours has to do it explicitly.
 *
 * Same intent as the `ecommerce: null` reset below, but it has to ride along
 * in the event push itself: GTM resolves variables when the tag fires, so a
 * separate reset push afterwards would clear the value the tag still needs.
 */
const FLAT_KEY_RESET: Record<string, undefined> = {
  transactionContactEmail: undefined,
};

function pushEcommerceEvent(
  event: string,
  ecommerce: GaEcommercePayload,
  options?: PushOptions,
): void {
  const dataLayer = getDataLayer();
  if (!dataLayer) {
    options?.onSent?.();
    return;
  }
  if (process.env.NODE_ENV === 'development') {
    console.debug('[ga4 ecommerce]', event, ecommerce);
  }

  // Object-shape pushes consumed by the GTM GA4 Event tag with "Send Ecommerce
  // data → Data Source: Data Layer". The null reset is per GA4 docs so items
  // from a prior event don't bleed into the next one. GTM overrides
  // `dataLayer.push`, so it can throw synchronously — every push is guarded so
  // a caller awaiting `onSent` is never left hanging.
  if (!options?.onSent) {
    try {
      dataLayer.push({ ecommerce: null });
      dataLayer.push({
        ...FLAT_KEY_RESET,
        ...options?.extra,
        event,
        ecommerce,
      });
    } catch {
      // Fire-and-forget event — nothing to resolve, just don't propagate.
    }
    return;
  }

  // The caller wants to wait until GTM has fired the tags before it navigates
  // away (the /checkout/result page redirects immediately after `purchase`).
  const timeoutMs = options.timeoutMs ?? 2000;
  let settled = false;
  const settle = (): void => {
    if (settled) return;
    settled = true;
    options.onSent?.();
  };

  // GTM is ad-blocked or not installed — nothing will ever invoke
  // `eventCallback`, so resolve now instead of stalling the redirect for the
  // full fallback timeout.
  if (!isGtmLoaded()) {
    settle();
    return;
  }

  // GTM invokes `eventCallback` once the tags for this push have fired;
  // `eventTimeout` guarantees it still fires if a tag hangs, and the extra
  // setTimeout is a final backstop if the callback never arrives.
  try {
    dataLayer.push({ ecommerce: null });
    dataLayer.push({
      ...FLAT_KEY_RESET,
      ...options.extra,
      event,
      ecommerce,
      eventCallback: settle,
      eventTimeout: timeoutMs,
    });
  } catch {
    // The push threw, so the tags will never fire — resolve now rather than
    // waiting on a callback that can't arrive.
    settle();
    return;
  }
  setTimeout(settle, timeoutMs + 100);
}

export function trackViewItem(params: {
  productKey: string;
  name?: string;
  category?: string;
  price?: number;
  currency?: string;
}): void {
  if (!params.productKey) return;
  const item: GaEcommerceItem = {
    item_id: params.productKey,
    item_name: params.name,
    item_category: params.category,
    price: params.price,
    quantity: 1,
  };
  pushEcommerceEvent('view_item', {
    currency: params.currency,
    value: params.price,
    items: [item],
  });
}

export function trackAddToCart(params: {
  productKey: string;
  name?: string;
  total: MoneyInput;
  coupon?: string;
}): void {
  if (!params.productKey) return;
  const value = moneyToFloat(params.total);
  const item: GaEcommerceItem = {
    item_id: params.productKey,
    item_name: params.name,
    price: value,
    quantity: 1,
  };
  pushEcommerceEvent('add_to_cart', {
    currency: params.total.currencyCode,
    value,
    items: [item],
    coupon: params.coupon,
  });
}

export function trackBeginCheckout(params: {
  productKey: string;
  name?: string;
  total: MoneyInput;
  coupon?: string;
}): void {
  if (!params.productKey) return;
  const value = moneyToFloat(params.total);
  const item: GaEcommerceItem = {
    item_id: params.productKey,
    item_name: params.name,
    price: value,
    quantity: 1,
  };
  pushEcommerceEvent('begin_checkout', {
    currency: params.total.currencyCode,
    value,
    items: [item],
    coupon: params.coupon,
  });
}

/**
 * Fires the GA4 `purchase` event. Resolves once GTM has sent the hit (or after
 * a short timeout), so callers can `await` this before redirecting away from
 * the result page — otherwise the navigation tears the page down before GTM
 * dispatches the request and the purchase is silently lost.
 *
 * `email` feeds Google Ads Enhanced Conversions: it is pushed as the flat
 * top-level key `transactionContactEmail`, the same key the legacy shop uses,
 * so the existing GTM chain (DLV `transactionContactEmail` → the
 * "User-Provided Data" variable `dlv_transactionContactEmail`) works unchanged
 * for the Chargebee checkout. Plain text on purpose — Google normalises and
 * SHA-256-hashes it itself before transmission, and hashing it here would
 * break that shared variable (and with it the legacy flow).
 */
export function trackPurchase(params: {
  transactionId: string;
  productKey: string;
  name?: string;
  total: MoneyInput;
  coupon?: string;
  email?: string;
}): Promise<void> {
  if (!params.productKey || !params.transactionId) return Promise.resolve();
  const value = moneyToFloat(params.total);
  const item: GaEcommerceItem = {
    item_id: params.productKey,
    item_name: params.name,
    price: value,
    quantity: 1,
  };
  const email = params.email?.trim();
  return new Promise((resolve) => {
    pushEcommerceEvent(
      'purchase',
      {
        transaction_id: params.transactionId,
        currency: params.total.currencyCode,
        value,
        items: [item],
        coupon: params.coupon,
      },
      {
        onSent: resolve,
        // `undefined` rather than '' when we have no email: it flushes the key
        // (see FLAT_KEY_RESET) so the DLV resolves as unset and the tag skips
        // `user_data`, instead of sending a hash of "".
        extra: { transactionContactEmail: email || undefined },
      },
    );
  });
}
